import type { AuthSession } from './types';

let current: AuthSession | null = null;
let generation = 0;
const listeners = new Set<() => void>();

/** Only a successful login or a previously verified, unexpired session sets this. */
export function setAccessSession(session: AuthSession | null): void {
	if (current?.token !== session?.token || scopeOf(current) !== scopeOf(session)) generation++;
	current = session;
	for (const listener of listeners) listener();
}

export function accessSession(): AuthSession | null { return current; }
export function accessGeneration(): number { return generation; }
export function accessScope(): string { return scopeOf(current); }
export function subscribeAccess(listener: () => void): () => void {
	listeners.add(listener);
	return () => { listeners.delete(listener); };
}

function scopeOf(session: AuthSession | null): string {
	if (!session) return '';
	// Roles and policy are partitioned too: a former owner never opens its
	// broad cached view while signed back in as a rep.
	return JSON.stringify([session.vendorId, session.memberId ?? 'company',
		session.eventId ?? 'unbound', session.vendorRole ?? 'legacy', session.vendorPolicy ?? 'legacy']);
}

export function assertAccess(expected: number): void {
	if (!current || generation !== expected) {
		throw Object.assign(new Error('Session changed. Sign in again before continuing.'), { status: 401 });
	}
}

export function mayDeleteLeads(): boolean {
	return !!current && (!current.vendorRole || current.vendorRole === 'owner'
		|| (current.vendorPolicy === 'vendor-team-v2' && current.vendorRole === 'manager'));
}

export function cachedRead<T>(key: string): T | null {
	const scope = accessScope();
	if (!scope) return null;
	try { return JSON.parse(localStorage.getItem(`scandeck.private.v1:${scope}:${key}`) ?? 'null') as T | null; }
	catch { return null; }
}

export function cacheWrite<T>(key: string, value: T, expected: number): void {
	assertAccess(expected);
	try { localStorage.setItem(`scandeck.private.v1:${accessScope()}:${key}`, JSON.stringify(value)); }
	catch { /* Memory remains usable when device storage is unavailable. */ }
}

export function purgeCurrentCache(): void {
	const prefix = `scandeck.private.v1:${accessScope()}:`;
	try {
		for (const key of Object.keys(localStorage)) if (key.startsWith(prefix)) localStorage.removeItem(key);
	} catch { /* best effort; the old scope is never reused for another role */ }
}
